import React, {
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import clsx from 'clsx';
import {useResizeDetector} from 'react-resize-detector';
import _ from 'underscore';
import {
	type Column,
	type ColumnPinningState,
	type ColumnSizingState,
	getCoreRowModel as defaultGetCoreRowModel,
	type Table as ITable,
	type RowData,
	type SortingState,
	type TableOptions,
	type Updater,
	useReactTable,
} from '@tanstack/react-table';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {useUpdateEffect} from '@netx/core-components/hooks/useUpdateEffect';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import useWindowEvent from '@netx/core-components/hooks/useWindowEvent';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	isActiveElementInputable,
	splitEventHandler,
} from '@netx/core-next/utils';
import {useColumnSizing, useVirtualizedTableRowSelection} from '../hooks';
import type {CustomVirtualizedSelection} from '../types';
import {TableBody, type TableBodyProps} from './TableBody';
import {
	TableBodyDraggable,
	type TableBodyDraggableProps,
} from './TableBodyDraggable';
import {
	getTableUtilityClass,
	TABLE_CLASS_NAME,
	tableClasses,
	type TableClasses,
} from './tableClasses';
import {TableHeader} from './TableHeader';
import {TablePagerVirtualized} from './TablePagerVirtualized';

type TableOwnerState = WithClasses<
	TableClasses,
	{
		isResizingColumn?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableOwnerState) => {
	const {classes, isResizingColumn} = ownerState;
	const slots = {
		root: ['root', isResizingColumn && 'isResizingColumn'],
		footer: ['footer'],
	};
	return composeClasses(slots, getTableUtilityClass, classes);
};

const TableRoot = styled('div', {
	name: TABLE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '1 1 auto',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	[`&:not(.${tableClasses.isResizingColumn})`]: {
		'& .TableCell-root': {
			transition: theme.transitions.create('width', {}),
		},
	},
}));

const TableContent = styled(ScrollBorder, {
	name: TABLE_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	flex: '1 1 auto',
	[`&.${scrollBorderClasses.hasOverflow}`]: {
		[`&.${scrollBorderClasses.scrolled}`]: {
			'& .TableHeader-root': {
				borderBottom: `2px solid ${theme.palette.custom.border}`,
			},
		},
		[`&:not(.${scrollBorderClasses.scrolledToEnd})`]: {
			'& .TableBody-root': {
				borderBottom: `1px solid ${theme.palette.custom.border}`,
			},
			[`& + .${tableClasses.footer}`]: {
				borderTop: `1px solid ${theme.palette.custom.border}`,
			},
		},
	},
	[`&.${scrollBorderClasses.hasHorizontalOverflow}`]: {
		[`&.${scrollBorderClasses.scrolledHorizontally}`]: {
			'& .TableCell-lastPinnedLeft': {
				borderRight: `1px solid ${theme.palette.custom.border}`,
			},
		},
		[`&:not(.${scrollBorderClasses.scrolledToEndHorizontally})`]: {
			'& .TableCell-firstPinnedRight': {
				borderLeft: `1px solid ${theme.palette.custom.border}`,
			},
		},
	},
}));

const TableFooter = styled('div', {
	name: TABLE_CLASS_NAME,
	slot: 'Footer',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	'&:not(:empty)': {
		padding: theme.spacing(1),
	},
	alignItems: 'center',
	borderTop: `1px solid transparent`,
}));

const TableFooterLeftSideContent = styled('div', {
	name: TABLE_CLASS_NAME,
	slot: 'FooterLeftSideContent',
})(({theme}) => ({
	paddingLeft: theme.spacing(1),
	flex: '1 1 auto',
}));

// get the index of the last column that has been resized by the user (ltr)
const getLastDirtiedColumnIndex = (
	columns: Column<any>[],
	dirtyColumns: Record<string, boolean>,
) =>
	Object.entries(dirtyColumns).reduce((acc, [key, value]) => {
		if (!value) return acc; // not dirty and shouldnt even be in the list
		const index = columns.findIndex((column) => column.id === key);
		return index > acc ? index : acc;
	}, -1);

export type CommonExtendedTableProps<
	TData extends RowData,
	SData extends SortingState = SortingState,
> = Pick<
	TableProps<TData, SData>,
	| 'data'
	| 'dataReady'
	| 'disabled'
	| 'disabledArrowFocusTrap'
	| 'disableRowDrag'
	| 'engagedRowId'
	| 'excludeIdsFromRowSelection'
	| 'hidePager'
	| 'isItemLoaded'
	| 'loadMoreItems'
	| 'minimumBatchSize'
	| 'page'
	| 'tableRef'
	| 'tableBodyRef'
	| 'rowSelection'
	| 'rowReorderEnabled'
	| 'showRowActive'
	| 'totalRows'
	| 'flagRow'
	| 'rowVirtualizerRef'
	| 'onRowClick'
	| 'onRowSelectionChange'
	| 'onRowContextMenu'
	| 'onRowDragEnd'
>;

export interface TableProps<
	TData extends RowData,
	SData extends SortingState = SortingState,
> extends WithClasses<
			TableClasses,
			Pick<
				TableOptions<TData>,
				| 'columns'
				| 'data'
				| 'getRowId'
				| 'onColumnPinningChange'
				| 'enableSubRowSelection'
				| 'enableRowSelection'
				| 'onSortingChange'
			>
		>,
		Pick<
			TableBodyProps<TData>,
			| 'allowDynamicRowHeight'
			| 'arrowFocusTrapRef'
			| 'disabledArrowFocusTrap'
			| 'engagedRowId'
			| 'getRowProgress'
			| 'getRowProps'
			| 'isItemLoaded'
			| 'loadMoreItems'
			| 'minimumBatchSize'
			| 'overscan'
			| 'rowActiveDescendantPrefix'
			| 'rowVirtualizerRef'
			| 'scrollIndex'
			| 'showRowActive'
			| 'subsetToShow'
			| 'subsetTotal'
			| 'totalRows'
			| 'flagRow'
			| 'onRowClick'
			| 'onRowContextMenu'
			| 'onStartIndexUpdate'
		>,
		Pick<
			TableBodyDraggableProps<TData>,
			'disableDrag' | 'onRowDragStart' | 'onRowDragEnd'
		> {
	children?: React.ReactNode;
	/** The current column pinning state */
	columnPinning?: ColumnPinningState;
	/** Should data be considered ready */
	dataReady?: boolean;
	/**
	 * If `true`, the table will be disabled
	 *
	 * Note: this currently only applies to the header columns
	 */
	disabled?: boolean;
	excludeIdsFromRowSelection?: string[];
	footerLeftSideContent?: React.ReactNode;
	headerHeight?: number;
	hidePager?: boolean;
	/** The current page number */
	page?: number;
	/** Size of a page */
	pageSize?: number;
	/** Allow row re-ordering via drag */
	rowReorderEnabled?: boolean;
	/**
	 * If `true`, row dragging will be disabled
	 *
	 * Note: this only applies when `rowReorderEnabled` is `true` and simply disables the drag
	 *
	 * @default false
	 */
	disableRowDrag?: boolean;
	/** The current row selection state */
	rowSelection?: CustomVirtualizedSelection;
	/** The current sorting state */
	sortingState?: SData;
	/** A `ref` to the table instance */
	tableRef?: React.RefObject<ITable<TData>>;
	tableBodyRef?: React.RefObject<HTMLDivElement>;
	/**
	 * Callback for when the page changes
	 * @param page New page number
	 * @returns
	 */
	onPageChange?: (page: number) => void;
	/**
	 * Callback for when the row selection changes
	 * @param selection New selection state
	 */
	onRowSelectionChange?: (
		selection:
			| CustomVirtualizedSelection
			| ((selection: CustomVirtualizedSelection) => CustomVirtualizedSelection),
	) => void;
}

export const TablePrimitive = <
	TData extends RowData,
	SData extends SortingState = SortingState,
>({
	classes: _classes,
	className,
	children,
	allowDynamicRowHeight,
	columns,
	data,
	dataReady,
	disableRowDrag,
	disabledArrowFocusTrap,
	disabled,
	engagedRowId,
	headerHeight,
	hidePager,
	totalRows,
	columnPinning,
	footerLeftSideContent,
	overscan,
	page,
	pageSize,
	rowSelection,
	rowActiveDescendantPrefix,
	rowReorderEnabled,
	rowVirtualizerRef,
	scrollIndex,
	showRowActive,
	sortingState,
	subsetTotal,
	subsetToShow,
	tableRef,
	tableBodyRef,

	flagRow,
	getRowId,
	getRowProgress,
	getRowProps,
	isItemLoaded,
	loadMoreItems,
	enableRowSelection,
	enableSubRowSelection,
	excludeIdsFromRowSelection,

	onColumnPinningChange,
	onPageChange,
	onRowClick,
	onRowContextMenu,
	onRowDragStart,
	onRowDragEnd,
	onRowSelectionChange,
	onSortingChange,
	onStartIndexUpdate,
	...props
}: TableProps<TData, SData>) => {
	const sharedTableRef = useSharedRef(null, [tableRef]);
	// reference to the scrollable area
	const scrollRef = useRef<HTMLDivElement>(null);
	// we need to track stage width
	// we use this to ensure that all rows are at least the
	// width of the viewable area (when longer than the longest row)
	// const [stageWidth, setStageWidth] = useState(0);

	// store column manual sizing
	const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
	const [calculatingColumns, setCalculatingColumns] = useState(false);
	// track columns that have been manually resized by the user
	const dirtyColumnSizing = useRef<Record<string, boolean>>({});
	// flag to track if the column sizing was updated programmatically so we do not
	// add any columns to the dirty list that shouldn't be there
	const justUpdatedColumnSizingProgrmatically = useRef(false);

	// track visible row range
	const [visibleRange, setVisibleRange] = useState({start: 0, stop: 0});
	// reference to the row virtualizer instance
	const rowVirtualizerInnerRef = useSharedRef(null, [rowVirtualizerRef]);

	const pageCount = Math.ceil(totalRows / (pageSize ?? 25));
	const currentPage = page ?? Math.floor(visibleRange.start / (pageSize ?? 25));

	// handle column sizing changes
	// passed directly to the react-table instance
	const onColumnSizingChange = useConstCallback(
		(updaterOrValue: Updater<ColumnSizingState>) => {
			const old = {...columnSizing};
			const result =
				typeof updaterOrValue === 'function'
					? updaterOrValue(columnSizing)
					: updaterOrValue;

			if (!justUpdatedColumnSizingProgrmatically.current) {
				dirtyColumnSizing.current = Object.entries(result)
					.filter(
						([key, value]) =>
							old[key] !== value || dirtyColumnSizing.current[key],
					)
					.reduce((acc, [key]) => ({...acc, [key]: true}), {});
			}
			justUpdatedColumnSizingProgrmatically.current = false;
			endCalculcatingColumns();
			setColumnSizing(result);
		},
	);

	// our virtualized selection hack
	// we have a chicken and egg problem with the react-table selection
	// so we must create this first and then wrap the table instance
	const {reactTableRowSelection, setReactTableRowSelection, wrapTable} =
		useVirtualizedTableRowSelection<TData>({
			excludeIdsFromRowSelection,
			totalRows,
			selection: rowSelection,
			onSelectionChange: onRowSelectionChange,
		});

	// when the data is not in a ready state we must ensure that we reset this
	useUpdateEffect(() => {
		if (!dataReady) {
			table.toggleAllRowsSelected(false);
		}
	}, [dataReady]);

	const {ref: targetRef, width: stageWidth = 0} = useResizeDetector({
		handleWidth: true,
		skipOnMount: false,
	});

	const sharedTableRootRef = useSharedRef<HTMLDivElement>(null, [targetRef]);

	const correctFocusScroll = useCallback(() => {
		if (
			scrollRef.current &&
			scrollTopRef.current !== scrollRef.current.scrollTop
		) {
			// setTimeout(() => {
			scrollRef.current!.scrollTop = scrollTopRef.current;
			// }, 0);
		}
	}, []);

	// TODO: what problem was this trying to solve
	// not running into any issues with it turned off
	// useWindowEvent('focus', () => {
	// 	if (
	// 		sharedTableRootRef.current &&
	// 		sharedTableRootRef.current.contains(document.activeElement)
	// 	) {
	// 		correctFocusScroll();
	// 	}
	// });

	// create react-table instance and wrap in our virtualized selection hack
	const table = wrapTable(
		useReactTable<TData>({
			// TODO: allow for passing in custom react-table options beyond what we
			// have exposed here (for easier outside customazation)
			getCoreRowModel: defaultGetCoreRowModel(),
			columnResizeMode: 'onChange',
			columns,
			data,
			enableColumnPinning: true,
			enableMultiRowSelection: enableRowSelection,
			enableRowSelection: enableRowSelection,
			enableSubRowSelection,
			// some custom state
			state: {
				rowSelection: reactTableRowSelection,
				columnPinning: columnPinning ?? {},
				columnSizing,
				sorting: sortingState,
			},
			getRowId,
			onColumnPinningChange,
			onColumnSizingChange,
			onRowSelectionChange: setReactTableRowSelection,
			onSortingChange,
		}),
	);

	// TODO: test performance of these on large column sets
	// we may want memoize these
	const leftLeafColumnWidths = table
		.getLeftLeafColumns()
		.map((column) => column.getSize());
	const rightLeafColumnWidths = table
		.getRightLeafColumns()
		.map((column) => column.getSize());

	// const leftLeafColumnsWidth = leftLeafColumnWidths.reduce(
	// 	(acc, width) => acc + width,
	// 	0,
	// );
	// const rightLeafColumnsWidth = rightLeafColumnWidths.reduce(
	// 	(acc, width) => acc + width,
	// 	0,
	// );

	// will be set as the width for all rows
	// const rowWidth = Math.max(table.getTotalSize(), stageWidth);

	// const isResizingColumn =
	// 	table.getState().columnSizingInfo.isResizingColumn !== false;

	// const {columnSizing, onColumnSizingChange} = useColumnSizing<TData>({
	// 	getTotalSize: () => table.getTotalSize(),
	// 	onColumnSizingChange: table.setColumnSizing,
	// 	columnPinning,
	// 	leftLeafColumnsWidth,
	// 	rightLeafColumnsWidth,
	// 	allLeafColumns: table.getAllLeafColumns(),
	// 	rowWidth,
	// 	isResizingColumn,
	// 	stageWidth,
	// });

	// table.setOptions((opts) => ({
	// 	...opts,
	// 	onColumnSizingChange,
	// 	state: {
	// 		...opts.state,
	// 		columnSizing,
	// 	},
	// }));

	// expose the table instance
	useImperativeHandle(sharedTableRef, () => table, [table]);

	const {rows} = table.getRowModel();
	const headerGroups = table.getHeaderGroups();

	// will be set as the width for all rows
	const rowWidth = Math.max(table.getTotalSize(), stageWidth);

	const isResizingColumn =
		table.getState().columnSizingInfo.isResizingColumn !== false;

	const initialColumnWidths = useMemo(() => {
		setColumnSizing({});
		return table
			.getAllLeafColumns()
			.reduce<Record<string, number>>(
				(acc, col) => ({...acc, [col.id]: col.getSize()}),
				{},
			);
	}, [columns]);

	const endCalculcatingColumnsTimeout = useRef<NodeJS.Timeout | null>(null);
	const endCalculcatingColumns = useConstCallback(() => {
		if (endCalculcatingColumnsTimeout.current) {
			clearTimeout(endCalculcatingColumnsTimeout.current);
		}
		endCalculcatingColumnsTimeout.current = setTimeout(() => {
			setCalculatingColumns(false);
		}, 100);
	});

	const thottledSetColumnSizing = useConstCallback(
		_.throttle(() => {
			const largestRowWidth = Math.ceil(table.getTotalSize());
			if (largestRowWidth === stageWidth) {
				endCalculcatingColumns();
				return;
			}

			const sizeDif = largestRowWidth / stageWidth;
			const columns = table.getAllLeafColumns();
			setCalculatingColumns(true);
			justUpdatedColumnSizingProgrmatically.current = true;
			// we will only update the column sizing for columns that are not pinned
			// we will not auto adjust anything the user has explicitely sized
			// table.setState(prev => ({
			// 	...prev,
			// 	columnSizingInfo: {
			// 		...prev.columnSizingInfo,
			// 		isResizingColumn: 'true',
			// 	}
			// }));
			table.setColumnSizing((prev) => {
				const lastDirtiedIndex = getLastDirtiedColumnIndex(
					columns,
					dirtyColumnSizing.current,
				);
				return columns.reduce((acc, column, index) => {
					if (
						lastDirtiedIndex > index ||
						columnPinning?.left?.includes(column.id) ||
						columnPinning?.right?.includes(column.id) ||
						dirtyColumnSizing.current[column.id]
					)
						return acc;

					// TODO: cleanup
					// if we only have one unpinned column, make it the full width minus pinned column widths
					if (
						columns.length -
							(columnPinning?.left?.length ?? 0) -
							(columnPinning?.right?.length ?? 0) ===
						1
					) {
						const totalLeft = leftLeafColumnWidths.reduce(
							(acc, width) => acc + width,
							0,
						);
						const totalRight = rightLeafColumnWidths.reduce(
							(acc, width) => acc + width,
							0,
						);
						return {
							...acc,
							[column.id]: Math.max(
								stageWidth - totalLeft - totalRight,
								initialColumnWidths[column.id],
							),
						};
					}

					return {
						...acc,
						[column.id]: Math.max(
							(prev[column.id] ?? initialColumnWidths[column.id]) / sizeDif,
							initialColumnWidths[column.id],
						),
					};
				}, prev);
			});
		}, 16),
	);

	useWhenChangedEffect(
		[
			initialColumnWidths,
			leftLeafColumnWidths,
			rightLeafColumnWidths,
			rowWidth,
			isResizingColumn,
			stageWidth,
		],
		() => {
			if (isResizingColumn) return;
			thottledSetColumnSizing();
		},
	);

	// TODO: this is for injecting a dummy cell to make the haader/body right pinned columns always up agains the right edge
	const lastSizeableColumnIndex = useMemo(() => {
		const columns = table.getAllLeafColumns();
		if (!columnPinning?.right) return columns.length;
		const index = columns.findIndex(
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			(column) => column.id === columnPinning.right![0],
		);
		return index;
	}, [columnPinning, columns]);

	// See hacks in the `TableContent` props
	// TODO: move to hook
	const scrollTopRef = useRef(0);
	const keydownAt = useRef(0);

	const ownerState: TableOwnerState = {
		classes: _classes,
		className,
		isResizingColumn: calculatingColumns || isResizingColumn,
	};

	const classes = useUtilityClasses(ownerState);

	// const detectVisibleColumns = useConstCallback(() => {
	// 	let allowedOneToLeft = false;
	// 	let allowedOneToRight = false;
	// 	const scrollLeft = scrollRef.current?.scrollLeft ?? 0;
	// 	const availableWidth = stageWidth - totalPinnedRightWidth;
	// 	const indexes = columnPositions.reduce<number[]>((acc, [x1, x2], index) => {
	// 		if (index < leftLeafColumnWidths.length) return [...acc, index];
	// 		if (index > columns.length - rightLeafColumnWidths.length - 1) return [...acc, index];
	// 		if (x2 < scrollLeft) return acc;
	// 		const previousColumn = columnPositions[index - 1];
	// 		if (previousColumn[1] < scrollLeft) {
	// 			if (!allowedOneToLeft) {
	// 				allowedOneToLeft = true;
	// 				return [...acc, index];
	// 			}
	// 			return acc;
	// 		}
	// 		if (x1 > scrollLeft + availableWidth) {
	// 			if (!allowedOneToRight) {
	// 				allowedOneToRight = true;
	// 				return [...acc, index];
	// 			}
	// 			return acc;
	// 		}
	// 		return [...acc, index];
	// 	}, []);
	// 	setVisibleColumnIndexes(indexes);
	// 	return indexes;
	// });

	// useEffect(() => {
	// 	detectVisibleColumns();
	// 	if (!scrollRef.current) return;
	// 	const listener = _.throttle((event: Event) => {
	// 		requestAnimationFrame(() => detectVisibleColumns());
	// 	}, 25);
	// 	scrollRef.current.addEventListener('scroll', listener, {passive: true});
	// 	return () => {
	// 		scrollRef.current?.removeEventListener('scroll', listener);
	// 	};
	// }, [columnSizing]);

	const TableBodyComponent = rowReorderEnabled ? TableBodyDraggable : TableBody;

	return (
		<TableRoot
			role="table"
			aria-rowcount={totalRows}
			className={clsx(classes.root, className)}
			ref={sharedTableRootRef}
			{...props}
			onKeyDown={(event) => {
				if (!enableRowSelection) return;
				if (event.metaKey && event.key === 'a') {
					if (isActiveElementInputable()) return;
					event.preventDefault();
					event.stopPropagation();
					table.toggleAllRowsSelected(true);
				}
			}}
		>
			<TableContent
				innerRef={scrollRef}
				hide
				limitContents={false}
				// TODO: this is a fix for a bug in chrome and safari (firefox seems alright)
				// where when a scrollable area, with a stick positioned element, will auto scroll
				// on focus from tabbing (not from clicking)
				// so, if our table header is 50px - when focusing into the table via tab, we get a scrollTop of 50px
				// i am leveraging a timeout and keydown/focus listener
				onFocusCapture={() => {
					const now = Date.now();
					if (keydownAt.current + 750 < now) return;
					correctFocusScroll();
				}}
				onKeyDown={(event) => {
					if (event.key === 'Tab') {
						scrollTopRef.current = scrollRef.current?.scrollTop || 0;
						keydownAt.current = Date.now();
						// console.log('storing scrollTop at ', scrollTopRef.current);
						return;
					}
					keydownAt.current = 0;
				}}
			>
				<TableHeader<TData>
					disabled={disabled}
					headerGroups={headerGroups}
					height={headerHeight}
					lastSizeableColumnIndex={lastSizeableColumnIndex}
					leftLeafColumnWidths={leftLeafColumnWidths}
					pinnedColumns={columnPinning}
					rightLeafColumnWidths={rightLeafColumnWidths}
					rowWidth={rowWidth}
				/>
				<TableBodyComponent<TData>
					ref={tableBodyRef}
					allowDynamicRowHeight={allowDynamicRowHeight}
					isItemLoaded={isItemLoaded}
					lastSizeableColumnIndex={lastSizeableColumnIndex}
					leftLeafColumnWidths={leftLeafColumnWidths}
					loadMoreItems={loadMoreItems}
					disabled={disabled}
					disabledArrowFocusTrap={disabledArrowFocusTrap}
					engagedRowId={engagedRowId}
					headerHeight={headerHeight}
					overscan={overscan}
					pinnedColumns={columnPinning}
					disableDrag={disableRowDrag || disabled}
					rightLeafColumnWidths={rightLeafColumnWidths}
					rowActiveDescendantPrefix={rowActiveDescendantPrefix}
					rowWidth={rowWidth}
					rows={rows}
					rowVirtualizerRef={rowVirtualizerInnerRef}
					scrollIndex={scrollIndex}
					scrollRef={scrollRef}
					showRowActive={showRowActive}
					stageWidth={stageWidth}
					subsetToShow={subsetToShow}
					subsetTotal={subsetTotal}
					table={table}
					totalRows={totalRows}
					flagRow={flagRow}
					getRowProgress={getRowProgress}
					getRowProps={getRowProps}
					onRowContextMenu={onRowContextMenu}
					onRowClick={
						!disabled && (enableRowSelection || onRowClick)
							? (row, modifiers) => {
									if (enableRowSelection) {
										// TODO: this should not be here unless we opted in for selection
										// perhaps even then this should be elsewhere
										if (modifiers.meta) {
											row.toggleSelected();
											return;
										}

										if (modifiers.shift) {
											row.toggleSelected(true, {
												// @ts-ignore
												shiftKey: modifiers.shift,
											});
											return;
										}
									}
									onRowClick?.(row, modifiers);
							  }
							: undefined
					}
					onRowDragStart={onRowDragStart}
					onRowDragEnd={onRowDragEnd}
					onStartIndexUpdate={splitEventHandler(
						onStartIndexUpdate,
						(startIndex, stopIndex) => {
							subsetToShow == null &&
								setVisibleRange({start: startIndex, stop: stopIndex});
						},
					)}
				/>
			</TableContent>
			<TableFooter className={classes.footer}>
				{(footerLeftSideContent != null || !hidePager) && (
					<TableFooterLeftSideContent>
						{footerLeftSideContent}
					</TableFooterLeftSideContent>
				)}
				{!hidePager && (
					<TablePagerVirtualized
						count={totalRows}
						disabled={disabled}
						loading={!dataReady}
						rowsPerPage={pageSize ?? 25}
						page={currentPage}
						// TODO: clean up this logic for a "clssic" pager
						// either a separate component of we tackle it here - preferably with a hook
						rangeEnd={
							subsetTotal != null
								? Math.min(
										currentPage * (pageSize ?? 0) + (pageSize ?? 1) - 1,
										totalRows - 1,
								  )
								: visibleRange.stop + currentPage * (pageSize ?? 0)
						}
						rangeStart={
							subsetTotal != null
								? currentPage * (pageSize ?? 0)
								: visibleRange.start + currentPage * (pageSize ?? 0)
						}
						showFirstButton={pageCount > 1}
						showLastButton={pageCount > 1}
						getItemAriaLabel={(type) => `Go to ${type} page`}
						onPageChange={(event, page) => {
							if (subsetTotal != null) {
								onPageChange?.(page);
								return;
							}
							rowVirtualizerInnerRef.current?.scrollToIndex(page * 25, {
								align: 'start',
							});
						}}
					/>
				)}
			</TableFooter>
			{children}
		</TableRoot>
	);
};

/**
 * `Table` is a React component that displays a dynamic table of data, supporting sorting, filtering, and selection.
 *
 * @typeParam TData The type of data that represents a row
 * @typeParam SData The sorting state type
 *
 * @example
 * ```tsx
 * <Table
 * 	columns={columns}
 * 	data={data}
 * 	dataReady={true}
 * 	totalRows={data.length}
 * 	getRowId={(row) => row.id.toString()}
 * />
 * ```
 */
export const Table = React.memo(TablePrimitive) as <
	TData extends RowData,
	SData extends SortingState = SortingState,
>(
	props: TableProps<TData, SData>,
) => JSX.Element;

export default Table;
